import { useEffect, useState, useMemo } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { videos, categories } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');

  useEffect(() => {
    setActiveSlug(slug || 'all');
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  const filteredVideos = useMemo(() => {
    if (activeSlug === 'all') return videos;
    return videos.filter((v) => v.categorySlug === activeSlug);
  }, [activeSlug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const pageTitle = currentCategory ? `${currentCategory.name} - 麻豆cm传媒在线高清` : '分类浏览 - 麻豆cm传媒在线高清';

  const breadcrumbItems = [
    { name: '首页', path: '/' },
    { name: '分类浏览', path: '/category/all' },
    ...(activeSlug !== 'all' && currentCategory ? [{ name: currentCategory.name }] : []),
  ];

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: `浏览麻豆cm传媒在线高清平台的${currentCategory ? currentCategory.name : '全部'}视频内容，海量高清资源等您发现。`,
    url: `https://madoucm.com/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '麻豆cm传媒在线高清',
      url: 'https://madoucm.com/',
    },
  };

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={`浏览麻豆cm传媒在线高清平台的${currentCategory ? currentCategory.name : '全部'}视频内容，海量高清资源等您发现。画质清晰流畅，免费在线观看。`} />
      <meta name="keywords" content={`麻豆cm传媒在线高清,${currentCategory ? currentCategory.name + ',' : ''}高清视频,在线播放,免费观看`} />
      <link rel="canonical" href={`https://madoucm.com/category/${activeSlug}`} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={`浏览麻豆cm传媒在线高清平台的${currentCategory ? currentCategory.name : '全部'}视频内容`} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`https://madoucm.com/category/${activeSlug}`} />
      <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>

      <Navbar />
      <main className="pt-20 pb-14 bg-background-50 min-h-screen">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
            {currentCategory ? currentCategory.name : '全部视频'}
          </h1>
          <p className="text-sm text-foreground-500 mb-8">
            共 <strong className="text-primary-500">{filteredVideos.length}</strong> 部高清视频
          </p>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`text-sm font-medium px-4 py-2 rounded-full whitespace-nowrap transition-all cursor-pointer border ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50 border-primary-500'
                    : 'bg-background-50 text-foreground-600 border-background-200/70 hover:border-primary-300 hover:text-primary-500'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredVideos.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-film-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无视频内容</p>
              <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 mt-2 inline-block cursor-pointer">
                返回全部视频
              </Link>
            </div>
          ) : (
            <div data-product-shop className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {filteredVideos.map((video) => (
                <Link
                  key={video.id}
                  to={`/article/${video.id}`}
                  className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer"
                >
                  <div className="relative w-full aspect-video overflow-hidden">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute top-2 left-2 bg-background-950/75 backdrop-blur-sm text-background-50 text-[10px] px-1.5 py-0.5 rounded">
                      {video.resolution}
                    </div>
                    <div className="absolute bottom-2 right-2 bg-background-950/75 backdrop-blur-sm text-background-50 text-[10px] px-1.5 py-0.5 rounded">
                      {video.duration}
                    </div>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/90 opacity-0 group-hover:opacity-100 transition-all transform scale-75 group-hover:scale-100">
                        <i className="ri-play-fill text-background-50 text-xl ml-0.5"></i>
                      </div>
                    </div>
                  </div>
                  <div className="p-3">
                    <h2 className="font-heading font-semibold text-foreground-900 text-sm line-clamp-1 group-hover:text-primary-500 transition-colors">
                      {video.title}
                    </h2>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[11px] text-foreground-400">{video.views} 次观看</span>
                      <span className="text-[11px] text-foreground-400">{video.date}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}